import React, { useState } from 'react'
import Loader from '../../../components/Loader'
import styled from 'styled-components'
import Colors from '../../../assets/styles/Colors'
import ScreenHeader from '../../../components/v2/ScreenHeader'
import Title from '../../../components/v2/Title'
import Responsive from '../../../assets/config/Responsive'
import { useTranslation } from 'react-i18next'
import NavigationButton from '../../../components/v2/buttons/NavigationButton'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../../../store'
import { CriticalIllnessState, setInsurerInfo } from '../../../store/criticalIllnessSlice'
import api from '../../../api'
import DialogModal from '../../../components/v2/modals/DialogModal'
import Accordion from '../../../components/v2/accordions/Accordion'
import dayjs from 'dayjs'
import Checkbox from '../../../components/v2/buttons/Checkbox'
import CriticalProgressbar from '../../../components/v2/progrees-bars/CriticalProgressbar'
import BackgroundIcon from '../../../components/v2/BackgroundIcon'
import { currencySymbol } from '../../../domain/types'
import { AppState } from '../../../store/appSlice'

const CheckInfo = () => {
  //
  const { lang } = useAppSelector((root: { app: AppState }) => root.app)
  //
  const isCriticalInfoLoading = false
  //
  const { filter, checkout, screenHeader } = useAppSelector((data: { critical: CriticalIllnessState }) => data.critical)
  //
  const { data: pageInfo, isLoading: isPageInfoLoading } = api.useGetPetPageInfoQuery('critical-check-info')
  //
  const pageTitle = pageInfo?.translations?.[lang]?.title
  const stepName = pageInfo?.translations?.[lang]?.stepName
  //
  // countries array from api
  const { data: countries, isLoading: isCountriesLoading } = api.useGetCountriesQuery(undefined)
  //
  const { data: productCategories, isLoading: isProductCategoryLoading } =
    api.useGetProductCategoriesListQuery(undefined)
  //
  const categoryId = productCategories?.find((x) => x.slug === 'critical')?.id || 0
  //
  const { data: informationsheetlist, isLoading: isInformationSheetListLoading } = api.useGetInformationSheetQuery({
    categoryId: categoryId,
    attachmentSlug: 'information-sheet',
  })
  //
  const itemID = informationsheetlist?.find((x) => x.productCategoryId === categoryId)?.id || 0
  //
  const { data: currentInformationSheet, isLoading: isCurrentInformationSheetLoading } =
    api.useGetInformationSheetDetailQuery(itemID, {
      skip: !itemID && true,
    })
  //
  const dispatch = useAppDispatch()
  //
  const { t } = useTranslation()
  //
  const navigate = useNavigate()
  //
  const [franiTermsModalIsOpen, setFraniTermsModalIsOpen] = useState<boolean>(false)
  //
  const [insurerCompanyTermsModalIsOpen, setInsurerCompanyTermsModalIsOpen] = useState<boolean>(false)
  //
  const closeFraniTermsModal = () => {
    setFraniTermsModalIsOpen(false)
  }
  //
  const closeInsurerCompanyTermsModal = () => {
    setInsurerCompanyTermsModalIsOpen(false)
  }
  //
  //
  const CheckBoxText =
    t('iReadAndAgree') +
    ' ' +
    filter?.offer?.brandName?.[lang]?.title +
    `${lang === 'ge' ? 'ს' : "'s"}` +
    ' ' +
    t('insuranceCondition')
  //

  const handlePrevButton = () => {
    if (checkout.forWho === 'for-me' || checkout.forWho === '') {
      navigate('/critical-for-me')
    }
    //
    if (checkout.forWho === 'for-other') {
      navigate('/critical-insurer')
    }
    //
    if (checkout.forWho === 'for-me-and-other') {
      navigate('/critical-for-me-and-other')
    }
  }

  // splittin image url for showing in the box as title
  const splittingImageTitle = (image: any) => {
    const url: string | URL = image

    const pathName = new URL(url).pathname

    const pathSegments = pathName.split('/')

    const fileName = pathSegments[pathSegments.length - 1]

    const titleSegment = fileName.split('-')

    const title = titleSegment.slice(1).join('-')

    return title
  }
  //
  //
  const MainLoader =
    isCriticalInfoLoading ||
    isCountriesLoading ||
    isCurrentInformationSheetLoading ||
    isInformationSheetListLoading ||
    isProductCategoryLoading ||
    isPageInfoLoading
  //

  return MainLoader ? (
    <Loader />
  ) : (
    <Wrapper>
      {/* frani terms modal */}
      <DialogModal
        title={`${t('franiConditions')}`}
        isOpen={franiTermsModalIsOpen}
        onClose={closeFraniTermsModal}
        src={currentInformationSheet?.attachment?.[lang]?.url || ''}
        isPdf={true}
        acceptOnClick={() => [
          dispatch(
            setInsurerInfo({
              fieldName: 'privacyTerm',
              fieldValue: true,
            }),
          ),
          closeFraniTermsModal(),
        ]}
        declineOnClick={() => [
          dispatch(
            setInsurerInfo({
              fieldName: 'privacyTerm',
              fieldValue: false,
            }),
          ),
          closeFraniTermsModal(),
        ]}
      />

      {/* insurance company terms */}
      <DialogModal
        title={CheckBoxText}
        isOpen={insurerCompanyTermsModalIsOpen}
        onClose={closeInsurerCompanyTermsModal}
        src={filter.offer?.productWording ?? filter.offer?.productWording}
        isPdf={true}
        acceptOnClick={() => [
          dispatch(
            setInsurerInfo({
              fieldName: 'policyTerm',
              fieldValue: true,
            }),
          ),
          closeInsurerCompanyTermsModal(),
        ]}
        declineOnClick={() => [
          dispatch(
            setInsurerInfo({
              fieldName: 'policyTerm',
              fieldValue: false,
            }),
          ),
          closeInsurerCompanyTermsModal(),
        ]}
      />

      <ScreenHeader totalCount={screenHeader.totalCount} passedCount={screenHeader.passedCount} />
      <Content>
        <Title text={pageTitle} />

        <CriticalProgressbar stepName={stepName} />

        {/* BEGIN Persons INFO */}
        {checkout.forWho && checkout.forWho !== 'for-me' ? (
          <PersonsBlock>
            {checkout.insured?.map((insurer, insurerIndex) => {
              return (
                <Accordion
                  key={insurerIndex}
                  isOpen={false}
                  editBtnOnchange={() => {
                    if (checkout.forWho === 'for-other') {
                      return navigate('/critical-for-other')
                    }

                    if (checkout.forWho === 'for-me-and-other') {
                      return navigate('/critical-for-me-and-other')
                    }
                  }}
                  editBtnText={t('edit')}
                  title={` ${
                    insurerIndex === 0 && checkout.forWho === 'for-me-and-other'
                      ? `${t('insurerInformation')}`
                      : `${t('insured')}`
                  } ${insurerIndex >= 1 ? insurerIndex + 1 : ''}`}
                >
                  <TextDiv>
                    <LeftText>{t('cityzenship')}</LeftText>
                    <RightText>
                      {countries?.find((x) => x.isoCode === insurer?.cityzenship)?.translations?.[lang]?.name}
                    </RightText>
                  </TextDiv>
                  <TextDiv>
                    <LeftText>{t('personalNumber')}</LeftText>
                    <RightText>{insurer?.idNumber}</RightText>
                  </TextDiv>
                  <TextDiv>
                    <LeftText>{t('dateOfBirth')}</LeftText>
                    <RightText>{insurer?.dateOfbirth?.toString()?.replace(/\s/g, ' ')?.split('-')[0]}</RightText>
                  </TextDiv>
                  {insurer?.translations?.ge?.firstName && (
                    <TextDiv>
                      <LeftText>{t('nameInGeorgia')}</LeftText>
                      <RightText>{insurer?.translations?.ge?.firstName}</RightText>
                    </TextDiv>
                  )}
                  {insurer?.translations?.ge?.lastName && (
                    <TextDiv>
                      <LeftText>{t('surnameInGeorgia')}</LeftText>
                      <RightText>{insurer?.translations?.ge?.lastName}</RightText>
                    </TextDiv>
                  )}
                  <TextDiv>
                    <LeftText>{t('nameInEnglish')}</LeftText>
                    <RightText>{insurer?.translations?.en?.firstName}</RightText>
                  </TextDiv>
                  <TextDiv>
                    <LeftText>{t('surnameInEnglish')}</LeftText>
                    <RightText>{insurer?.translations?.en?.lastName}</RightText>
                  </TextDiv>
                  <TextDiv>
                    <LeftText>{t('gender')}</LeftText>
                    <RightText>
                      {insurer.gender === 'female' && `${t('female')}`}
                      {insurer.gender === 'male' && `${t('male')}`}
                    </RightText>
                  </TextDiv>
                  <hr />
                  <TextDiv>
                    <LeftText>{t('side1')}</LeftText>
                    <RightText>{insurer.IdImageFront ? splittingImageTitle(insurer.IdImageFront) : ''}</RightText>
                  </TextDiv>
                  <TextDiv>
                    <LeftText>{t('side2')}</LeftText>
                    <RightText>{insurer.IdImageBack ? splittingImageTitle(insurer.IdImageBack) : ''}</RightText>
                  </TextDiv>

                  {insurer.insuredRecently === true && (
                    <>
                      <hr />

                      <TextDiv>
                        <LeftText style={{ width: '100%' }}>{t('beenInsuredRecently')}</LeftText>
                      </TextDiv>
                    </>
                  )}
                </Accordion>
              )
            })}
          </PersonsBlock>
        ) : null}
        {/* END Persons INFO */}
        {/*  */}

        {/*  */}
        {/* BEGIN FOR ME & FOR OTHER INFO */}
        {checkout.forWho === 'for-me' || checkout.forWho === 'for-other' ? (
          <Block>
            <Accordion
              isOpen={true}
              editBtnOnchange={() => {
                if (checkout.forWho === 'for-me') {
                  navigate('/critical-for-me')
                }
                //
                if (checkout.forWho === 'for-other') {
                  navigate('/critical-insurer')
                }
              }}
              editBtnText={t('edit')}
              title={`${t('yourPersonalInfo')}`}
            >
              <TextDiv>
                <LeftText>{t('cityzenship')}</LeftText>
                <RightText>
                  {countries?.find((x) => x.isoCode === checkout.insurer?.cityzenship)?.translations?.[lang]?.name}
                </RightText>
              </TextDiv>

              <TextDiv>
                <LeftText>{t('personalNumber')}</LeftText>
                <RightText>{checkout?.insurer?.idNumber}</RightText>
              </TextDiv>

              <TextDiv>
                <LeftText>{t('dateOfBirth')}</LeftText>
                <RightText>{checkout?.insurer?.dateOfbirth?.toString()?.replace(/\s/g, '')?.split('-')}</RightText>
              </TextDiv>

              {checkout?.insurer?.cityzenship === 'GE' && (
                <TextDiv>
                  <LeftText>{t('nameInGeorgia')}</LeftText>
                  <RightText>{checkout.insurer?.translations?.ge?.firstName}</RightText>
                </TextDiv>
              )}

              {checkout?.insurer?.cityzenship === 'GE' && (
                <TextDiv>
                  <LeftText>{t('surnameInGeorgia')}</LeftText>
                  <RightText>{checkout?.insurer?.translations?.ge?.lastName}</RightText>
                </TextDiv>
              )}

              <TextDiv>
                <LeftText>{t('nameInEnglish')}</LeftText>
                <RightText>{checkout?.insurer?.translations?.en?.firstName}</RightText>
              </TextDiv>

              <TextDiv>
                <LeftText>{t('surnameInEnglish')}</LeftText>
                <RightText>{checkout?.insurer?.translations?.en?.lastName}</RightText>
              </TextDiv>

              <TextDiv>
                <LeftText>{t('gender')}</LeftText>
                <RightText>
                  {checkout?.insurer?.gender === 'female' && `${t('female')}`}
                  {checkout?.insurer?.gender === 'male' && `${t('male')}`}
                </RightText>
              </TextDiv>

              <hr />
              <TextDiv>
                <LeftText>{t('side1')}</LeftText>
                <RightText>
                  {checkout?.insurer?.IdImageFront ? splittingImageTitle(checkout?.insurer?.IdImageFront) : ''}
                </RightText>
              </TextDiv>
              <TextDiv>
                <LeftText>{t('side2')}</LeftText>
                <RightText>
                  {checkout?.insurer?.IdImageBack ? splittingImageTitle(checkout?.insurer?.IdImageBack) : ''}
                </RightText>
              </TextDiv>

              {checkout.insurer?.insuredRecently === true && (
                <>
                  <hr />

                  <TextDiv>
                    <div>{t('beenInsuredRecently')}</div>
                  </TextDiv>
                </>
              )}
            </Accordion>
          </Block>
        ) : null}
        {/* END FOR ME & FOR OTHER INFO */}
        {/*  */}

        <Block>
          <Accordion
            isOpen={true}
            editBtnOnchange={() => alert('edit btn')}
            editBtnText={t('edit')}
            hideEditBtn={true}
            title={`${t('policyInfo')}`}
            disableEditBtn={true}
          >
            <TextDiv>
              <LeftText>{t('insuranceCompany')}</LeftText>
              <RightText>{filter?.offer?.brandName?.[lang]?.title}</RightText>
            </TextDiv>
            <TextDiv>
              <LeftText>{t('category')}</LeftText>
              <RightText>{t('criticalInsurance')}</RightText>
            </TextDiv>
            <TextDiv>
              <LeftText>{t('policyDuration')}</LeftText>
              <RightText>{parseInt(filter.chooseDays ? filter?.chooseDays : '1') * 12 + ' ' + t('month')}</RightText>
            </TextDiv>
            <TextDiv>
              <LeftText>{t('startDate')}</LeftText>
              <RightText>{filter.dates?.start ? dayjs(filter.dates?.start).format('DD MMMM YYYY') : ''}</RightText>
            </TextDiv>
            <TextDiv>
              <LeftText>{t('endDate')}</LeftText>
              <RightText>{filter.dates?.end ? dayjs(filter.dates?.end).format('DD MMMM YYYY') : ''}</RightText>
            </TextDiv>
            <TextDiv>
              <LeftText>{t('limit')}</LeftText>
              <RightText>
                {filter.limit?.toString()?.replace(/\B(?=(\d{3})+(?!\d))/g, ' ') +
                  ` ${currencySymbol(filter.currency || 'gel')}`}
              </RightText>
            </TextDiv>
            <hr />
            <TextDiv>
              <LeftText>{t('paymentSchedule')}</LeftText>
              <RightText>{filter.paymentSchedule?.type === 'MONTHLY' ? `${t('monthly')}` : `${t('annual')}`}</RightText>
            </TextDiv>
            <TextDiv>
              <LeftText>{t('currentPayment')}</LeftText>
              <RightText>{filter.offer?.calculatedPrices.monthly} ₾</RightText>
            </TextDiv>
            <TextDiv>
              <LeftText>{t('eachPayment')}</LeftText>
              <RightText>{filter.offer?.calculatedPrices.monthly} ₾</RightText>
            </TextDiv>
            <TextDiv>
              <LeftText>{t('totalAmount')}</LeftText>
              <RightText>{filter.offer?.calculatedPrices.annual} ₾</RightText>
            </TextDiv>

            <hr />
            <Checkbox
              id="check-information-privacy-terms"
              label={`${t('franiConditions')}`}
              defaultChecked={checkout.insurer.privacyTerm || false}
              onClick={() => setFraniTermsModalIsOpen(true)}
            />

            <hr />
            <Checkbox
              id="check-information-company-terms"
              label={CheckBoxText}
              defaultChecked={checkout.insurer.policyTerm || false}
              onClick={() => setInsurerCompanyTermsModalIsOpen(true)}
            />
          </Accordion>
        </Block>

        <Buttons>
          <NavigationButton text={`${t('back')}`} buttonType="prev" onClick={handlePrevButton} />
          <NavigationButton
            text={`${t('next')}`}
            buttonType="next"
            disabled={false}
            onClick={() => navigate('/critical-policy-registration')}
          />
        </Buttons>
      </Content>

      <BackgroundIcon />
    </Wrapper>
  )
}
const RightText = styled.div`
  text-align: right;
`
const LeftText = styled.div`
  width: 55%;
`
const TextDiv = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: ${Colors.nevadaGrey};
  font-size: 14px;
  &:not(:last-child) {
    margin-bottom: 14px;
  }
`
const Block = styled.div`
  max-width: 464px;
  width: 100%;
  margin-inline: auto;
  margin-top: 32px;

  ${Responsive.mobile} {
    margin-top: 24px;
  }
`

const PersonsBlock = styled(Block)`
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: 1fr;
  grid-row-gap: 32px;

  ${Responsive.mobile} {
    grid-row-gap: 24px;
  }
`

const Buttons = styled.div`
  display: flex;
  gap: 20px;
  width: min-content;
  margin-top: 48px;
  margin-inline: auto;
  button {
    width: 175px;
  }

  ${Responsive.mobile} {
    margin-top: 40px;
    gap: 16px;
    button {
      width: 140px;
    }
  }
`

const Content = styled.div`
  position: relative;
  margin-top: 58px;
  z-index: 2;
  padding-inline: 20px;
  width: 100%;

  ${Responsive.mobile} {
    margin-top: 26px;
  }
`

const Wrapper = styled.div`
  min-height: 100vh;
  background-color: ${Colors.backgroundGrey};
  padding-bottom: 20px;
  /*  */
`

export default CheckInfo
